import { contrastingText, parseHex, relativeLuminance, shade } from './contrast.js';
import { resolveDarkTokens } from './dark.js';
import { DEFAULT_LAYOUT, constrainLayout, type LayoutConfig, type PartialLayoutConfig } from './layout.js';
import { DEFAULT_TEMPLATE_ID, getTemplate } from './templates.js';
import { DEFAULT_TOKENS, TOKEN_KEYS, type ThemeTokens } from './tokens.js';

/**
 * Three-tier resolution (CLAUDE.md §5): default -> template -> shop overrides,
 * injected into `:root` at runtime. No build, no deploy, realtime preview.
 */

export interface ShopTheme {
  template: string;
  overrides?: ThemeTokens;
  /** Dark-mode overrides, kept separate so one cannot bleed into the other. */
  darkOverrides?: ThemeTokens;
  /**
   * §5 (v2): `auto` follows the device, which is what almost every shop wants.
   * `light` pins the storefront to the palette the merchant previewed — a shop
   * whose brand only works on white can say so.
   */
  colorScheme?: ColorScheme;
  layout?: PartialLayoutConfig;
  logo?: string | null;
  cover?: string | null;
}

export type ColorScheme = 'auto' | 'light' | 'dark';

export interface ResolvedTheme {
  templateId: string;
  tokens: Required<ThemeTokens>;
  /** The same tokens as they render at night (§5 dark-mode-aware templates). */
  darkTokens: Required<ThemeTokens>;
  colorScheme: ColorScheme;
  layout: LayoutConfig;
  logo: string | null;
  cover: string | null;
}

export const EMPTY_SHOP_THEME: ShopTheme = { template: DEFAULT_TEMPLATE_ID };

export function resolveTheme(
  theme: ShopTheme | null | undefined,
  vertical: 'retail' | 'fnb' | 'services' = 'retail',
): ResolvedTheme {
  const input = theme ?? EMPTY_SHOP_THEME;
  const template = getTemplate(input.template) ?? getTemplate(DEFAULT_TEMPLATE_ID)!;

  const tokens = {
    ...DEFAULT_TOKENS,
    ...template.tokens,
    ...sanitizeOverrides(input.overrides),
  } as Required<ThemeTokens>;

  // Guardrail: whatever primary the merchant picked, the text on it stays
  // readable. The merchant only ever chooses the primary colour (§5), so this
  // is the one derived value we must always recompute.
  if (input.overrides?.['color-primary']) {
    tokens['color-primary-contrast'] = contrastingText(tokens['color-primary']);
  }

  const layout = constrainLayout(
    { ...DEFAULT_LAYOUT, ...template.layout, ...(input.layout ?? {}) },
    vertical,
  );

  return {
    templateId: template.id,
    tokens,
    darkTokens: resolveDarkTokens(tokens, template.dark, sanitizeOverrides(input.darkOverrides)),
    colorScheme: input.colorScheme ?? 'auto',
    layout,
    logo: input.logo ?? null,
    cover: input.cover ?? null,
  };
}

/** Drop unknown keys so a stale/hand-edited JSONB blob cannot inject CSS. */
function sanitizeOverrides(overrides: ThemeTokens | undefined): ThemeTokens {
  if (!overrides) return {};
  const out: ThemeTokens = {};
  for (const key of TOKEN_KEYS) {
    const value = overrides[key];
    if (typeof value === 'string' && value.length <= 120 && !/[;{}<>]/.test(value)) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Derived variables the components use but no template defines by hand —
 * hover/active shades computed from the primary so every template gets them
 * for free.
 */
export function derivedTokens(tokens: Required<ThemeTokens>): Record<string, string> {
  const primary = tokens['color-primary'];
  const bg = parseHex(tokens['color-bg']);
  // On a dark storefront, "hover" must get lighter, not darker.
  const isDarkSurface = bg ? relativeLuminance(bg) < 0.2 : false;
  return {
    'color-primary-hover': shade(primary, isDarkSurface ? 0.12 : -0.12),
    // The soft tint sits on the ground, so it moves toward the ground: nearly
    // white on a light theme, nearly black on a dark one. Lightening it on a
    // dark storefront would produce a glowing chip nobody asked for.
    'color-primary-soft': shade(primary, isDarkSurface ? -0.78 : 0.86),
    'color-overlay': 'rgba(0, 0, 0, 0.45)',
  };
}

/** The `style` attribute value for `:root` — this is how themes ship. */
export function themeToCssVariables(
  resolved: ResolvedTheme,
  scheme: 'light' | 'dark' = 'light',
): Record<string, string> {
  const source = scheme === 'dark' ? resolved.darkTokens : resolved.tokens;
  const vars: Record<string, string> = {};
  for (const key of TOKEN_KEYS) {
    vars[`--${key}`] = source[key];
  }
  for (const [key, value] of Object.entries(derivedTokens(source))) {
    vars[`--${key}`] = value;
  }
  return vars;
}

function block(vars: Record<string, string>, selector: string): string {
  const body = Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  return `${selector} {\n${body}\n}`;
}

/**
 * The stylesheet a storefront ships (§5: injected at runtime, no build, no
 * deploy).
 *
 * Light is always the base declaration, so a viewer whose browser reports
 * nothing still gets a complete palette. Dark arrives twice: once behind
 * `prefers-color-scheme` for the device default, and once behind
 * `[data-color-scheme="dark"]` so an explicit choice wins over the device in
 * both directions.
 */
export function themeToCssText(resolved: ResolvedTheme, selector = ':root'): string {
  const light = block(themeToCssVariables(resolved, 'light'), selector);
  if (resolved.colorScheme === 'light') return light;

  const darkVars = themeToCssVariables(resolved, 'dark');
  const explicit = block(darkVars, `${selector}[data-color-scheme="dark"]`);

  // A shop pinned to dark needs no media query — it is dark for everyone.
  if (resolved.colorScheme === 'dark') return `${block(darkVars, selector)}\n\n${explicit}`;

  const auto = [
    '@media (prefers-color-scheme: dark) {',
    block(darkVars, `${selector}:not([data-color-scheme="light"])`)
      .split('\n')
      .map((line) => `  ${line}`)
      .join('\n'),
    '}',
  ].join('\n');

  return `${light}\n\n${auto}\n\n${explicit}`;
}
